import type { ReactNode } from 'react'
import { usePress } from './press'
import { Emblem, type EmblemName } from './brand/Emblems'
import { cx } from './cx'

export type SealState = 'ready' | 'busy' | 'disabled' | 'cancel'

export type SealProps = {
  emblem: EmblemName
  label: string
  /** Short hint under the label (desktop only). */
  hint?: ReactNode
  state?: SealState
  /** May return a promise: the seal stays busy and ignores presses until it settles. */
  onPress?: () => void | Promise<unknown>
  className?: string
}

/** The combat console's «Подтвердить ход». Guards against double submission. */
export function Seal({ emblem, label, hint, state = 'ready', onPress, className }: SealProps) {
  const { busy, disabled, press } = usePress(onPress, state === 'disabled', state === 'busy')
  return (
    <button
      type="button"
      className={cx('seal', state === 'disabled' && 'is-disabled', state === 'cancel' && 'seal--cancel', busy && 'is-busy', className)}
      onClick={press}
      disabled={disabled}
      aria-busy={busy || undefined}
    >
      <span className="seal__disc"><Emblem name={emblem} /></span>
      <b>{busy ? 'Подождите…' : label}</b>
      {hint && <small>{hint}</small>}
    </button>
  )
}

/** The seal as a full-width bar (phone consoles: battle, conversation). */
export function SealBar({ emblem, label, state = 'ready', onPress, className }: Omit<SealProps, 'hint'>) {
  const { busy, disabled, press } = usePress(onPress, state === 'disabled', state === 'busy')
  return (
    <button type="button" className={cx('sealbar', state === 'disabled' && 'is-disabled', busy && 'is-busy', className)} onClick={press} disabled={disabled} aria-busy={busy || undefined}>
      <Emblem name={emblem} />{busy ? 'Подождите…' : label}
    </button>
  )
}
